/**
 * Slider class.
 * 
 * Only use this component to create/initialize Splide slider.
 *
 * @module components/slider
 */

import Component from './component';
import functions from '../utils/functions';
import { getModule } from '../utils/modules';

export default (($, /* eslint-disable */ window, undefined /* eslint-enable */) => {
  'use strict';

  /**
   * Slider component class
   */
  class Slider extends Component {
    /**
     * Constructor
     */
    constructor(element, options = {}) {
      super(element, options);
      this.name = 'slider';
      this.options = $.extend(true, {}, Slider.default, options);
    }

    /**
     * Main initializer
     * 
     * @returns void
     */
    init() {
      this.loaded = false;
      this.mounted = false;

      // if @splidejs/splide is not loaded yet, load it now and postpone the init until it's loaded
      if (!Slider.Splide) {
        getModule('splide')
          .then(Splide => {
            Slider.Splide = Splide;
            this.init();
          })
          .catch(err => {
            this.logError(err);
          });

        return;
      }

      this.loaded = true;
      this.initSlider();
    }

    /**
     * Init Splide slider.
     * 
     */
    initSlider() {
      const options = this.options;

      this.destroySlider();

      // disable drag/swipe on editing mode
      if (functions.isExperienceEditorMode()) {
        options.sliderOptions = $.extend(options.sliderOptions || {}, {
          drag: false
        });
      }

      this.slider = new Slider.Splide(this.element[0], options.sliderOptions);

      if (options.updateTabIndex) {
        this.slider.on('moved', this._onMoved.bind(this));
      }

      if (options.adjustPageSize) {
        this.slider.on('resized', functions.debounce(this._onResized.bind(this)));
      }

      this.slider.on('mounted', this._onMounted.bind(this));

      for (var k in options.listeners) {
        if (functions.objectHasOwnProperty(options.listeners, k) && typeof options.listeners[k] === 'function' && typeof k === 'string') {
          this.slider.on(k, options.listeners[k]);
        }
      }

      this.slider.mount();
    }

    /**
     * Destroy Splide slider.
     */
    destroySlider() {
      if (this.slider) {
        this.slider.Components.Elements.getSlides(true).forEach(slide => {
          $(':input, a, img', slide.slide).add(slide.slide).removeAttr('tabindex');
        });

        this.slider.destroy(true);
        this.slider = null;
        this.mounted = false;
      }
    }

    /**
     * Refresh the Slider
     * 
     * @param {Object} options 
     * @param {Boolean} suppressRefresh 
     */
    setOptions(options, suppressRefresh) {
      if (this.slider) {
        this.slider.options = options;
        if (suppressRefresh !== true) {
          this.slider.refresh();
        }
      }
    }

    /**
     * Slider resize handler
     * 
     * Perform manual calculation for 'slide per page'.
     * This is required for the arrows to have correct state.
     */
    adjustPageSize() {
      if (this.slider && this.options.adjustPageSize) {
        const perPage = Math.floor($(this.slider.Components.Elements.track).width() / $(this.slider.Components.Elements.slides[0]).outerWidth());
        if (this.slider.options.perPage !== perPage) {
          this.slider.options = {
            perPage
          };
        }
      }
    }

    /**
     * Update tabindex on the slides.
     * Allow tabbing only on current slides.
     * 
     * @param {Integer} [newIndex]
     */
    updateTabIndex(newIndex) {
      if (this.slider && this.options.updateTabIndex) {
        // go through all slides, remove/add collapsed class
        this.slider.Components.Elements.getSlides(true).forEach(slide => {
          let isActive = false;

          if (typeof newIndex !== 'undefined') {
            if (newIndex === slide.index) {
              isActive = true;
            }
          }
          else {
            isActive = slide.isVisible();
          }

          if (isActive) {
            $(':input, a, img', slide.slide).add(slide.slide).removeAttr('tabindex');
          }
          else {
            $(':input, a, img', slide.slide).add(slide.slide).attr('tabindex', -1);
          }
        });
      }
    }

    /**
     * Splide 'resized' event handler.
     */
    _onResized() {
      this.adjustPageSize();
      this.updateTabIndex();
    }

    /**
     * Splide 'moved' event handler.
     */
    _onMoved() {
      this.updateTabIndex();
    }

    /**
     * Splide 'mounted' event handler.
     */
    _onMounted() {
      this.mounted = true;

      // adjust/calculate page size
      this.adjustPageSize();

      // update tab index
      this.updateTabIndex();
    }
  }

  Slider.selectors = {};

  Slider.default = {
    sliderOptions: {
      autoWidth: true,
      perPage: 1,
      perMove: 1,
      pagination: false,
      slideFocus: false,
      updateOnMove: true
    },
    adjustPageSize: false,
    updateTabIndex: true
  };

  return Slider;
})(jQuery, window);